import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { eventsAPI } from '@/api/events'
import { LiveEvent } from '@/types/event'
import Header from '@/components/common/Header'
import Loading from '@/components/common/Loading'

function EventDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [event, setEvent] = useState<LiveEvent | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadEvent()
    }
  }, [id])

  const loadEvent = async () => {
    try {
      setIsLoading(true)
      const data = await eventsAPI.getEvent(id!)
      setEvent(data)
    } catch (err: any) {
      setError('イベント情報の読み込みに失敗しました')
      console.error('Failed to load event:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleJoinLive = async () => {
    try {
      if (id) {
        await eventsAPI.joinEvent(id)
        navigate(`/events/${id}/live`)
      }
    } catch (err) {
      console.error('Failed to join event:', err)
      alert('イベントへの参加に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <Loading />
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'イベントが見つかりません'}
          </div>
          <button
            onClick={() => navigate('/')}
            className="mt-4 btn-secondary"
          >
            ホームに戻る
          </button>
        </div>
      </div>
    )
  }

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'yyyy年M月d日 HH:mm', { locale: ja })
    } catch {
      return dateStr
    }
  }

  const getStatusBadge = () => {
    switch (event.status) {
      case 'live':
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-500 text-white font-semibold rounded-full">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            ライブ配信中
          </span>
        )
      case 'scheduled':
        return (
          <span className="px-3 py-1 bg-blue-100 text-blue-700 font-semibold rounded-full">
            開催予定
          </span>
        )
      case 'ended':
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 font-semibold rounded-full">
            終了
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="mb-4 text-gray-600 hover:text-gray-900 flex items-center gap-2"
        >
          ← イベント一覧に戻る
        </button>

        <div className="card">
          <div className="flex items-start justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 flex-1">
              {event.title}
            </h1>
            {getStatusBadge()}
          </div>

          {event.description && (
            <p className="text-gray-700 mb-6 whitespace-pre-wrap">
              {event.description}
            </p>
          )}

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-semibold">開始時間:</span>
              <span>{formatDateTime(event.start_time)}</span>
            </div>

            {event.end_time && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="font-semibold">終了時間:</span>
                <span>{formatDateTime(event.end_time)}</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-semibold">イベントタイプ:</span>
              <span className="capitalize">{event.event_type}</span>
            </div>

            {event.current_participants !== undefined && (
              <div className="flex items-center gap-2 text-gray-600">
                <span className="font-semibold">現在の参加者:</span>
                <span>{event.current_participants}人</span>
                {event.max_participants && (
                  <span className="text-gray-500">
                    / 最大{event.max_participants}人
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-semibold">チャット:</span>
              <span>{event.chat_enabled ? '有効' : '無効'}</span>
            </div>

            <div className="flex items-center gap-2 text-gray-600">
              <span className="font-semibold">録画:</span>
              <span>{event.recording_enabled ? '有効' : '無効'}</span>
            </div>
          </div>

          {event.status === 'live' && (
            <div className="pt-6 border-t">
              <button
                onClick={handleJoinLive}
                className="w-full btn-primary py-3 text-lg"
              >
                ライブ配信に参加する
              </button>
            </div>
          )}

          {event.status === 'scheduled' && (
            <div className="pt-6 border-t">
              <p className="text-center text-gray-600">
                このイベントはまだ開始されていません
              </p>
            </div>
          )}

          {event.status === 'ended' && (
            <div className="pt-6 border-t">
              <p className="text-center text-gray-600">
                このイベントは終了しました
              </p>
              {event.recording_enabled && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  録画版は後日公開予定です
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default EventDetail